import React, { useState } from 'react';
import { LayoutDashboard, Smartphone, Search, TrendingUp, ArrowUpRight, Star, MapPin, Phone, CheckCircle2, ShoppingBag, DollarSign, Filter, Layers, ShoppingCart, MessageSquare, Settings, Bell, ChevronRight, Check } from 'lucide-react';
import { EcommerceRealMockup } from '../ui/EcommerceRealMockup';
import { GoogleOfficialProfileMockup } from '../ui/GoogleOfficialProfileMockup';
import { BiolinkBentoRealMockup } from '../ui/BiolinkBentoRealMockup';

export const InteractiveHeroShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'ecommerce' | 'google' | 'biolink'>('dashboard');

  return (
    <div className="w-full max-w-5xl mx-auto font-sans text-left">
      
      {/* MOCKUP CONTAINER (WINDOW FRAME LIGHT STYLE GOOGLE / THE BRAVA) */}
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xl overflow-hidden">
         
         {/* Top Browser Bar & Interactive Tabs */}
         <div className="bg-[#F8FAFC] border-b border-slate-200 px-4 pt-3 pb-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* macOS Window Controls & Breadcrumb */}
            <div className="flex items-center gap-2 pb-3 sm:pb-0">
               <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
               </div>
               <span className="text-[11px] font-mono text-slate-500 ml-2 hidden md:inline flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  youmachine.app/real-software-demos
               </span>
            </div>

            {/* Interactive Showcase Tabs */}
            <div className="flex gap-1 overflow-x-auto w-full sm:w-auto no-scrollbar">
               {[
                  { id: 'dashboard', label: 'Painel Genesis 360°', icon: LayoutDashboard },
                  { id: 'ecommerce', label: 'E-Commerce Suís Real', icon: ShoppingBag },
                  { id: 'google', label: 'Google Business Profile', icon: Search },
                  { id: 'biolink', label: 'Linkwi Bio Link 2.0', icon: Smartphone },
               ].map((tab) => (
                  <button
                     key={tab.id}
                     onClick={() => setActiveTab(tab.id as any)}
                     className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all border-t border-x ${
                        activeTab === tab.id
                           ? 'bg-white text-[#0061FF] border-slate-200 border-b-transparent shadow-xs'
                           : 'bg-transparent text-slate-500 border-transparent hover:text-slate-900'
                     }`}
                  >
                     <tab.icon className="w-3.5 h-3.5" />
                     <span>{tab.label}</span>
                  </button>
               ))}
            </div>
         </div>

         {/* MAIN DISPLAY WITH REAL SOFTWARE RECREATIONS */}
         <div className="p-4 md:p-6 bg-[#F8FAFC]">
            
            {/* TAB 1: PAINEL GENESIS 360° ENGINE */}
            {activeTab === 'dashboard' && (
               <div className="space-y-6 animate-fade-in bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-[#F8FAFC] p-4 rounded-2xl border border-slate-200/80">
                     <div>
                        <span className="text-[10px] font-black text-[#0061FF] uppercase tracking-wider">ECOSSISTEMA OPERACIONAL YOUMACHINE</span>
                        <h4 className="text-xl font-black text-slate-900">Painel Central • Gestão 360°</h4>
                        <span className="text-xs text-slate-500 font-medium">Chapecó/SC • Operação em tempo real sem intermediários</span>
                     </div>
                     <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-xl text-xs font-bold flex items-center gap-1 shrink-0">
                        <ArrowUpRight className="w-3.5 h-3.5" /> +34.2% Faturamento
                     </span>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                     <div className="bg-white border border-slate-200/80 p-3.5 rounded-2xl space-y-1 shadow-xs">
                        <span className="text-[10px] font-bold text-slate-400 block">Vendas no Mês</span>
                        <strong className="text-lg font-black text-slate-900 block">R$ 148.920,00</strong>
                        <span className="text-[9px] text-emerald-600 font-bold">100% Livre de Taxas</span>
                     </div>
                     <div className="bg-white border border-slate-200/80 p-3.5 rounded-2xl space-y-1 shadow-xs">
                        <span className="text-[10px] font-bold text-slate-400 block">Pedidos Realizados</span>
                        <strong className="text-lg font-black text-[#0061FF] block">3.428</strong>
                        <span className="text-[9px] text-slate-500 font-medium">98.2% taxa de entrega</span>
                     </div>
                     <div className="bg-white border border-slate-200/80 p-3.5 rounded-2xl space-y-1 shadow-xs">
                        <span className="text-[10px] font-bold text-slate-400 block">Leads Google Maps</span>
                        <strong className="text-lg font-black text-purple-700 block">1.890</strong>
                        <span className="text-[9px] text-slate-500 font-medium">Contatos diretos</span>
                     </div>
                     <div className="bg-white border border-slate-200/80 p-3.5 rounded-2xl space-y-1 shadow-xs">
                        <span className="text-[10px] font-bold text-slate-400 block">ROAS Anúncios</span>
                        <strong className="text-lg font-black text-emerald-600 block">8.5x</strong>
                        <span className="text-[9px] text-slate-500 font-medium">Retorno em Ads</span>
                     </div>
                  </div>
               </div>
            )}

            {/* TAB 2: E-COMMERCE REALISTA (SUÍS COSMÉTICOS RECRIAÇÃO FIEL) */}
            {activeTab === 'ecommerce' && (
               <div className="animate-fade-in">
                  <EcommerceRealMockup />
               </div>
            )}

            {/* TAB 3: GOOGLE BUSINESS PROFILE OFICIAL */}
            {activeTab === 'google' && (
               <div className="animate-fade-in">
                  <GoogleOfficialProfileMockup />
               </div>
            )}

            {/* TAB 4: LINKWI BIO LINK 2.0 */}
            {activeTab === 'biolink' && (
               <div className="animate-fade-in">
                  <BiolinkBentoRealMockup />
               </div>
            )}

         </div>

         {/* Bottom Status Bar */}
         <div className="bg-[#F8FAFC] border-t border-slate-200 px-6 py-3 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5 font-medium">
               <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Demonstração interativa de código real em produção
            </span>
            <span className="font-bold text-slate-900 hidden sm:inline">Genesis 360° SaaS Engine</span>
         </div>

      </div>
    </div>
  );
};
